/**
 * **Ce que Suivora a changé, dit par ceux qui s'en servent** (#2523).
 *
 * Un témoignage par fonctionnalité qui change le quotidien. La structure est ici — l'icône, la
 * personne, son accord ; le texte vient de la langue lue (`contenu.<langue>.ts`, bloc
 * `temoignages.items`), fusionné **par rang**, comme les autres sections de la vitrine.
 *
 * ⚠️ **Aucun témoignage ne paraît sans l'accord écrit de la personne citée.** Une version
 * précédente de la vitrine publiait des témoignages fictifs ; ils ont été retirés. Un faux avis se
 * vérifie en un appel, coûte la confiance qu'il prétend construire, et c'est une pratique
 * commerciale trompeuse (Code de la consommation, art. L121-2 et s.).
 *
 * Les textes des langues sont donc des **propositions** : on les fait relire et corriger par la
 * personne, on reporte sa version, puis on renseigne `accordRecuLe`. Tant qu'aucun témoignage n'a
 * d'accord, la section **ne se rend pas** — la règle de la bande des entreprises (#2345).
 *
 * ⚠️ **Jamais de chiffre qu'on n'a pas.** « Deux heures gagnées par semaine » se lit comme une
 * mesure ; si personne ne l'a mesuré, c'est une donnée inventée. Une garde refuse les chiffres dans
 * les citations : si la personne en donne un, c'est elle qui l'assume, et on lève la garde pour
 * cette ligne en connaissance de cause.
 */
import {
  FileSignature,
  type LucideIcon,
  Megaphone,
  Receipt,
  ScrollText,
  Upload,
} from 'lucide-react';

export type AuteurDeTemoignage = {
  /** Prénom et initiale du nom — « Mélanie S. » : assez pour être vrai, pas plus que consenti. */
  nom: string;
  /** L'entreprise, telle qu'elle s'écrit. */
  entreprise: string;
};

export type Temoignage = {
  /** Stable, pour les clés de rendu et la relecture — pas affiché. */
  id: string;
  icon: LucideIcon;
  /** `null` tant qu'on ne sait pas qui le portera : le texte reste une proposition. */
  auteur: AuteurDeTemoignage | null;
  /**
   * Date (AAAA-MM-JJ) de l'accord écrit de la personne **sur le texte publié**. `null` = pas
   * d'accord : le témoignage ne paraît pas.
   *
   * ⚠️ L'accord porte sur les mots : si le texte change après, il se redemande.
   */
  accordRecuLe: string | null;
};

/**
 * Les cinq fonctionnalités, dans l'ordre des textes de `temoignages.items`.
 *
 * Chaque proposition décrit un écran qui existe — rien de promis que le produit ne fasse pas.
 */
export const TEMOIGNAGES: readonly Temoignage[] = [
  /* Factures et CRA déposés sur le portail des clients qui refusent l'e-mail (#2509). */
  {
    id: 'depot-portail',
    icon: Upload,
    auteur: { nom: 'Mélanie S.', entreprise: 'Freemakers Consulting' },
    accordRecuLe: '2026-09-11',
  },
  /* Le compteur du bon de commande, vu par le porté pendant qu'il déclare (#2159). */
  {
    id: 'bons-de-commande',
    icon: ScrollText,
    auteur: { nom: 'Franck E.', entreprise: 'Freemakers Consulting' },
    accordRecuLe: '2026-09-11',
  },
  /* Les communiqués, et qui les a lus. */
  {
    id: 'communiques',
    icon: Megaphone,
    auteur: { nom: 'Seynabou N.', entreprise: 'Freemakers Consulting' },
    accordRecuLe: '2026-09-11',
  },
  /* La signature intégrée, sans abonnement à un outil tiers (#2496). */
  {
    id: 'signature-integree',
    icon: FileSignature,
    auteur: { nom: 'William L.', entreprise: 'Freemakers Consulting' },
    accordRecuLe: '2026-09-11',
  },
  /* Du CRA validé à la facture. */
  {
    id: 'cra-facture',
    icon: Receipt,
    auteur: { nom: 'Libert F.', entreprise: 'Freemakers Consulting' },
    accordRecuLe: '2026-09-11',
  },
];

/** Ceux qui peuvent paraître : une personne nommée, et son accord. */
export const temoignagesPublies = <T extends Temoignage>(liste: readonly T[]): T[] =>
  liste.filter(t => t.auteur != null && t.accordRecuLe != null);

/**
 * **Le temps de lire une citation** avant qu'elle ne laisse sa place, en secondes (#2527).
 *
 * Un rythme fixe coupe les longues citations au milieu et fait attendre sur les courtes : environ
 * trois mots et demi par seconde, jamais moins de huit secondes.
 */
export const dureeDeLecture = (citation: string): number =>
  Math.max(8, Math.round(citation.trim().split(/\s+/).length / 3.5));
